import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixDevcenterScriptsV1ScriptProperties from './devcenter-scripts-v1-script-properties-embedded-scripts.http.js';

export interface ScriptProperties {
  /**
   * Dynamic parameters available to the script when it runs on the site.
   *
   * Learn more about [using dynamic parameters](https://dev.wix.com/docs/wix-cli/guides/extensions/site-extensions/embedded-scripts/embedded-script-extension-files-and-code#using-dynamic-parameters-in-your-html-code).
   */
  parameters?: Record<string, string>;
  /** Whether to disable the script. Default: `false`. */
  disabled?: boolean;
}

export interface EmbedScriptRequest {
  /** Details of the script to embed. */
  properties: ScriptProperties;
  /**
   * Extension ID of the embedded script.
   *
   * You can find the extension ID in the **Extensions** page in the [app dashboard](https://manage.wix.com/account/custom-apps). If you added the embedded script extension [using the Wix CLI](https://dev.wix.com/docs/wix-cli/guides/extensions/site-extensions/embedded-scripts/add-an-embedded-script-extension), you can also find it in the extension's [`embedded.extension.ts` file](https://dev.wix.com/docs/wix-cli/guides/extensions/site-extensions/embedded-scripts/embedded-script-extension-files-and-code#embeddedextensionts).
   * @format GUID
   */
  componentId?: string | null;
}

export interface EmbedScriptResponse {
  /** Details of the embedded script. */
  properties?: ScriptProperties;
}

export interface GetEmbeddedScriptRequest {
  /**
   * Extension ID of the embedded script to retrieve.
   *
   * You can find the extension ID in the **Extensions** page in the [app dashboard](https://manage.wix.com/account/custom-apps). If you added the embedded script extension [using the Wix CLI](https://dev.wix.com/docs/wix-cli/guides/extensions/site-extensions/embedded-scripts/add-an-embedded-script-extension), you can also find it in the extension's [`embedded.extension.ts` file](https://dev.wix.com/docs/wix-cli/guides/extensions/site-extensions/embedded-scripts/embedded-script-extension-files-and-code#embeddedextensionts).
   * @format GUID
   */
  componentId?: string | null;
}

export interface GetEmbeddedScriptResponse {
  /** Details of the retrieved embedded script. */
  properties?: ScriptProperties;
}

export interface EmbedScriptByInstanceIdRequest {
  /** @format GUID */
  instanceId?: string;
  /** Parameters to embed */
  properties?: ScriptProperties;
  /** @format GUID */
  componentId?: string | null;
}

export interface GetEmbeddedScriptByInstanceIdRequest {
  /** @format GUID */
  instanceId?: string;
  /** @format GUID */
  componentId?: string | null;
}

/**
 * Inserts custom script tags into a site, with the specified values for any dynamic parameters. Call this method when your app is installed on a site.
 *
 * Your app must have an existing
 * [embedded script component](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/site-extensions/embedded-scripts/about-embedded-scripts),
 * with exactly matching parameter names.
 * @param properties - Details of the script to embed.
 * @public
 * @requiredField properties
 * @permissionId APPS.MANAGE_EMBEDDED_SCRIPT
 * @applicableIdentity APP
 * @fqn com.wixpress.market.aim.api.EmbeddedScriptsService.EmbedScript
 */
export async function embedScript(
  properties: ScriptProperties,
  options?: EmbedScriptOptions
): Promise<NonNullablePaths<EmbedScriptResponse, `properties.disabled`, 3>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    properties: properties,
    componentId: options?.componentId,
  });

  const reqOpts =
    ambassadorWixDevcenterScriptsV1ScriptProperties.embedScript(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          properties: '$[0]',
          componentId: '$[1].componentId',
        },
        singleArgumentUnchanged: false,
      },
      ['properties', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface EmbedScriptOptions {
  /**
   * Extension ID of the embedded script.
   *
   * You can find the extension ID in the **Extensions** page in the [app dashboard](https://manage.wix.com/account/custom-apps). If you added the embedded script extension [using the Wix CLI](https://dev.wix.com/docs/wix-cli/guides/extensions/site-extensions/embedded-scripts/add-an-embedded-script-extension), you can also find it in the extension's [`embedded.extension.ts` file](https://dev.wix.com/docs/wix-cli/guides/extensions/site-extensions/embedded-scripts/embedded-script-extension-files-and-code#embeddedextensionts).
   * @format GUID
   */
  componentId?: string | null;
}

/**
 * Retrieves information about your app's existing embedded script.
 *
 * If your app doesn't have an embedded script on the relevant site, a `404` error is returned.
 * @public
 * @permissionId APPS.MANAGE_EMBEDDED_SCRIPT
 * @applicableIdentity APP
 * @returns Details of the retrieved embedded script.
 * @fqn com.wixpress.market.aim.api.EmbeddedScriptsService.GetEmbeddedScript
 */
export async function getEmbeddedScript(
  options?: GetEmbeddedScriptOptions
): Promise<NonNullablePaths<ScriptProperties, `disabled`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    componentId: options?.componentId,
  });

  const reqOpts =
    ambassadorWixDevcenterScriptsV1ScriptProperties.getEmbeddedScript(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.properties!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { componentId: '$[0].componentId' },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GetEmbeddedScriptOptions {
  /**
   * Extension ID of the embedded script to retrieve.
   *
   * You can find the extension ID in the **Extensions** page in the [app dashboard](https://manage.wix.com/account/custom-apps). If you added the embedded script extension [using the Wix CLI](https://dev.wix.com/docs/wix-cli/guides/extensions/site-extensions/embedded-scripts/add-an-embedded-script-extension), you can also find it in the extension's [`embedded.extension.ts` file](https://dev.wix.com/docs/wix-cli/guides/extensions/site-extensions/embedded-scripts/embedded-script-extension-files-and-code#embeddedextensionts).
   * @format GUID
   */
  componentId?: string | null;
}
